import { useState } from 'react';
import { CASOS, EJEMPLOS_PLAYGROUND, colorQS } from '../lib/rutas-creativas.ts';
import { simular, type Simulacion } from '../adapters/http.ts';
import { Icon } from '../components/Icon.tsx';
/**
 * Vistas de contenido estático. La orden las autoriza a permanecer estáticas, pero exige
 * que vivan en la fuente editable — y acá viven, versionadas y modificables.
 */
/**
 * Las siete capas, con su NATURALEZA a la vista.
 *
 * Antes era una lista `<ol>` sin jerarquía: siete renglones iguales donde el
 * gate de control humano —lo único que no se puede saltear en todo el sistema—
 * pesaba lo mismo que un paso intermedio. Dirección lo marcó: la sección no
 * acompañaba al resto de la plataforma.
 *
 * El color no decora, clasifica: entrada, proceso, gate y entrega. Y el gate
 * destaca porque ES la pieza distinta, no porque quede lindo.
 */
type Naturaleza = 'entrada' | 'proceso' | 'gate' | 'entrega';

const CAPAS: ReadonlyArray<{
  n: string; titulo: string; nat: Naturaleza; desc: string; icono?: 'lock' | 'check';
}> = [
  { n: '1', titulo: 'Brief',           nat: 'entrada', desc: 'El pedido humano, con su objetivo y alcance.' },
  { n: '2', titulo: 'Decision Engine', nat: 'proceso', desc: 'Traduce el pedido en un plan y registra la regla aplicada.' },
  { n: '3', titulo: 'Creative IR',     nat: 'proceso', desc: 'Representación intermedia determinista.' },
  { n: '4', titulo: 'Scene Graph',     nat: 'proceso', desc: 'Estructura visual, backend-agnóstica.' },
  { n: '5', titulo: 'Render Plan',     nat: 'proceso', desc: 'Plan de render para el adaptador.' },
  { n: '6', titulo: 'Human Review',    nat: 'gate',    desc: 'El gate que no se puede saltear.', icono: 'lock' },
  { n: '7', titulo: 'Entrega',         nat: 'entrega', desc: 'Sólo lo aprobado.', icono: 'check' },
];

const ETIQUETA: Record<Naturaleza, string> = {
  entrada: 'Entrada', proceso: 'Proceso', gate: 'Gate obligatorio', entrega: 'Entrega',
};

export function Architecture() {
  return (
    <section>
      <p className="lede">Las capas por las que pasa cada brief, del input a la entrega.</p>

      <div className="capas__leyenda">
        {(['entrada', 'proceso', 'gate', 'entrega'] as const).map((k) => (
          <span className={`capas__ley capas__ley--${k}`} key={k}>
            <span className="capas__punto" />{k}
          </span>
        ))}
      </div>

      <ol className="capas">
        {CAPAS.map((c) => (
          <li className={`capa capa--${c.nat}`} key={c.n}>
            <span className="capa__nodo" aria-hidden="true">
              {c.icono ? <Icon name={c.icono} size={20} /> : c.n}
            </span>
            <div className="capa__cuerpo">
              <div className="capa__head">
                <h3 className="capa__titulo">{c.titulo}</h3>
                <span className="capa__tag">{ETIQUETA[c.nat]}</span>
              </div>
              <p className="capa__desc">{c.desc}</p>
            </div>
          </li>
        ))}
      </ol>

      <p className="note">
        El motor que ejecuta estas capas no es un diagrama: vive en <code>core/creative-intelligence</code>,
        y desde el 21 de julio las siete corren de punta a punta —incluido el render de video, sobre
        infraestructura propia—.
      </p>
    </section>
  );
}


export function UseCases() {
  return (
    <section>
      <p className="lede">
        Qué recorrido hace Nexus para cada tipo de pedido: la ruta que elige, las herramientas
        que usa y en cuánto tiempo entrega.
      </p>

      <div className="rows">
        {CASOS.map((c) => (
          <article className="row row--caso" key={c.name + c.ruta.code}>
            <span className="caso__icon"><Icon name={c.icon} size={20} /></span>

            <div className="row__main">
              <div className="row__name">{c.name}</div>
              <div className="row__meta mono">{c.ruta.tools.join(' → ')}</div>
            </div>

            <div className="caso__col">
              <span className="caso__k">Ruta</span>
              <span className="caso__v">{c.ruta.code} · {c.ruta.name}</span>
            </div>

            <div className="caso__col">
              <span className="caso__k">Tiempo</span>
              <span className="caso__v">{c.ruta.time}</span>
            </div>

            <span className={`chip-state chip-state--${colorQS(c.ruta.quality)}`}>
              QS {c.ruta.quality}
            </span>
          </article>
        ))}
      </div>

      <p className="note">
        Las rutas y sus tiempos son doctrina del sistema, no dato operativo: no cambian por
        proyecto. Quién elige cuál es el Decision Engine, no la interfaz.
      </p>
    </section>
  );
}

export function Playground() {
  const [texto, setTexto] = useState('');
  const [plazo, setPlazo] = useState('');
  const [corriendo, setCorriendo] = useState(false);
  const [r, setR] = useState<Simulacion | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function correr() {
    setCorriendo(true); setError(null); setR(null);
    const res = await simular(texto, plazo);
    setCorriendo(false);
    if (typeof res === 'string') { setError(res); return; }
    setR(res);
  }

  return (
    <section>
      <p className="lede">
        Escribí un brief corto y mirá cómo <em>piensa</em> Nexus: qué ruta elige, qué plan
        arma, cuánto estima cada tarea. <strong>No compromete nada</strong> — es un ensayo.
      </p>

      <div className="panel">
        <div className="panel__head">
          <span className="eyebrow">Tu brief</span>
          <span className="panel__rule" />
        </div>

        <textarea
          aria-label="Tu brief"
          value={texto}
          onChange={(ev) => setTexto(ev.target.value)}
          placeholder="Ej.: Quiero una landing para un operador logístico."
        />

        <div className="chips">
          {EJEMPLOS_PLAYGROUND.map((e) => (
            <button key={e} type="button" className="chip" onClick={() => setTexto(e)}>{e}</button>
          ))}
        </div>

        <div className="panel__cols" style={{ marginTop: 18 }}>
          <div>
            <label className="field__label" htmlFor="pg-plazo">¿Para cuándo? (opcional)</label>
            <input id="pg-plazo" value={plazo} onChange={(ev) => setPlazo(ev.target.value)}
                   placeholder="Ej.: en 2 horas · hoy · mañana" />
          </div>
        </div>

        <div className="enviar">
          <button type="button" className="btn-primary" disabled={corriendo || texto.trim().length < 8}
                  onClick={() => void correr()}>
            {corriendo ? 'Pensando…' : 'Simular'}
          </button>
          {r && <span className="recibido" role="status"><span className="recibido__dot" />Ensayo, no compromete nada</span>}
        </div>

        {error && <p className="notice notice--error" role="alert">{error}</p>}
      </div>

      {r && (
        <div className="acuse acuse--propuesta" style={{ marginTop: 22 }}>
          <div className="acuse__head">
            <span className="eyebrow">Así lo resolvería · sin comprometer</span>
            <span className="panel__rule" />
          </div>
          <p className="acuse__veredicto">{r.veredicto}</p>

          <dl className="acuse__datos">
            <div><dt>Ruta</dt><dd>{r.ruta} · {r.rutaNombre}</dd></div>
            <div><dt>Plazo leído</dt><dd>{r.plazoLeido ?? 'no se indicó'}</dd></div>
            <div className="acuse__motivo"><dt>Por qué esta ruta</dt><dd>{r.motivo}</dd></div>
          </dl>

          {r.plan && (
            <>
              <dl className="acuse__pacto">
                <div><dt>Qué recibirías</dt><dd><strong>{r.plan.entregable}</strong> — {r.plan.nivel}</dd></div>
                <div><dt>Complejidad</dt><dd>{r.plan.complejidad}</dd></div>
                <div><dt>Plataformas</dt><dd>{r.plan.plataformas.join(' · ')}</dd></div>
                <div><dt>Duración</dt><dd><strong>{r.plan.minutos} minutos</strong> desde que aprobás</dd></div>
              </dl>
              <ol className="acuse__roadmap">
                {r.plan.roadmap.map((t, i) => (
                  <li key={i}><span className="acuse__paso">{t.label}</span><span className="acuse__min">{t.min} min</span></li>
                ))}
              </ol>
            </>
          )}

          <p className="note">
            Esto es un ensayo: no se creó ningún pedido ni quedó nada registrado. Para que
            arranque de verdad, cargalo en <strong>Nuevo Brief</strong>.
          </p>
        </div>
      )}
    </section>
  );
}

export function Manual() {
  return (
    <section>
      <p className="lede">
        Manual de Procedimientos completo: cómo funciona Nexus Creative Platform de punta a punta,
        los gates de control humano y la matriz de plataformas. Consultá cualquier duda acá.
      </p>

      {/* El manual interactivo, servido como asset. Va en un iframe para que
          conserve su navegacion propia sin colisionar con los estilos de la app.
          Abrir en pestana aparte queda a un clic, por si preferis pantalla completa. */}
      <div className="manual-embed">
        <iframe
          className="manual-embed__frame"
          src="/manual-nexus-creative-os.html"
          title="Manual de Procedimientos - Nexus Creative Platform"
          loading="lazy"
        />
      </div>

      <p className="note">
        Lo queres a pantalla completa? <a href="/manual-nexus-creative-os.html" target="_blank" rel="noopener">Abrilo en una pestana nueva</a>.
        Las convenciones de control humano estan detalladas adentro.
      </p>
    </section>
  );
}

export function Faq() {
  const qa: readonly (readonly [string, string])[] = [
    ['¿Qué es Nexus Creative Platform?', 'Un Creative Control Center: coordina el trabajo creativo desde el brief hasta la entrega, con control humano en cada gate.'],
    ['¿Los datos que veo son reales?', 'Sí. Se leen en vivo de la base del sistema, con tu sesión iniciada. Nada de lo que ves está inventado ni es de muestra.'],
    ['¿Puedo cargar un brief?', 'Sí. Escribís qué necesitás en «Nuevo Brief», el sistema arma un plan con su roadmap y su tiempo, y vos lo aprobás o pedís una versión más chica. Recién ahí arranca.'],
    ['¿Cuánto puede tardar una entrega?', 'Una hora como máximo. Si por su complejidad necesita más, el sistema te propone hasta una hora adicional y vos decidís. Nunca más de dos horas, y el reloj arranca cuando aprobás.'],
    ['¿Qué pasa si rechazo un plan por el tiempo?', 'El sistema te devuelve una contrapropuesta con menos entregables y menor complejidad, que entra en menos tiempo. La aceptás o no.'],
    ['¿Qué pasa si una integración falla?', 'Se muestra su estado real. No se disimula ni se rellena con un ejemplo.'],
  ];
  return (
    <section>
      <dl className="faq">
        {qa.map(([q, a]) => (<div key={q}><dt>{q}</dt><dd>{a}</dd></div>))}
      </dl>
    </section>
  );
}

export function NotFound({ path }: { path: string }) {
  return (
    <section>
      <p className="notice notice--error" role="alert">
        La ruta <code>{path}</code> no corresponde a ninguna vista. No se inventa una pantalla en su lugar.
      </p>
    </section>
  );
}
